/**
 * ShapeTextToolbar - Rich text formatting toolbar for shapes
 * Appears when editing text inside shapes
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Type,
  Palette,
  Minus,
  X,
} from 'lucide-react';
import { TextStyle } from '../editing/types';

interface ShapeTextToolbarProps {
  textStyle: TextStyle;
  onStyleChange: (updates: Partial<TextStyle>) => void;
  position?: { x: number; y: number };
  /** Bounds of the element being edited in screen coordinates */
  elementBounds?: { x: number; y: number; width: number; height: number };
  /** Container element for viewport bounds calculation */
  containerRef?: React.RefObject<HTMLDivElement>;
  onClose?: () => void;
}

export const ShapeTextToolbar: React.FC<ShapeTextToolbarProps> = ({
  textStyle,
  onStyleChange,
  position,
  elementBounds,
  containerRef,
  onClose,
}) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showFontSizeMenu, setShowFontSizeMenu] = useState(false);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const fontSizeMenuRef = useRef<HTMLDivElement>(null);
  const [toolbarSize, setToolbarSize] = useState({ width: 0, height: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const [isPositionedAbove, setIsPositionedAbove] = useState(false);
  
  // Measure toolbar size
  useEffect(() => {
    if (toolbarRef.current) {
      const rect = toolbarRef.current.getBoundingClientRect();
      setToolbarSize({ width: rect.width, height: rect.height });
    }
  }, []);

  // Trigger animation on mount
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(target) &&
        toolbarRef.current &&
        !toolbarRef.current.contains(target)
      ) {
        setShowColorPicker(false);
      }
      if (
        fontSizeMenuRef.current &&
        !fontSizeMenuRef.current.contains(target) &&
        toolbarRef.current &&
        !toolbarRef.current.contains(target)
      ) {
        setShowFontSizeMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate viewport-aware position
  const calculatePosition = () => {
    const TOOLBAR_HEIGHT = 50; // Approximate toolbar height
    const TOOLBAR_MARGIN = 20; // Margin from element
    const VIEWPORT_PADDING = 10; // Padding from viewport edges
    
    // Get container bounds for viewport calculation
    const containerBounds = containerRef?.current?.getBoundingClientRect() || {
      top: 0,
      left: 0,
      right: window.innerWidth,
      bottom: window.innerHeight,
      width: window.innerWidth,
      height: window.innerHeight,
    };
    
    let x = position?.x || 0;
    let y = position?.y || 0;
    let positionedAbove = false;
    
    if (elementBounds) {
      // Calculate available space above and below the element
      const spaceAbove = elementBounds.y - containerBounds.top;
      const spaceBelow = containerBounds.bottom - (elementBounds.y + elementBounds.height);
      
      // Position horizontally centered on element
      x = elementBounds.x + elementBounds.width / 2;
      
      // Position above or below based on available space
      if (spaceAbove >= TOOLBAR_HEIGHT + TOOLBAR_MARGIN) {
        // Position above element
        y = elementBounds.y - TOOLBAR_MARGIN;
        positionedAbove = true;
      } else if (spaceBelow >= TOOLBAR_HEIGHT + TOOLBAR_MARGIN) {
        // Position below element
        y = elementBounds.y + elementBounds.height + TOOLBAR_HEIGHT + TOOLBAR_MARGIN;
        positionedAbove = false;
      } else {
        // Not enough space above or below, position at top of viewport
        y = containerBounds.top + VIEWPORT_PADDING + TOOLBAR_HEIGHT;
        positionedAbove = true;
      }
    } else if (position) {
      // Fallback: Check if there's enough space above the position
      const spaceAbove = position.y - containerBounds.top;
      if (spaceAbove < TOOLBAR_HEIGHT + TOOLBAR_MARGIN) {
        // Not enough space above, position below
        y = position.y + TOOLBAR_HEIGHT + TOOLBAR_MARGIN * 2;
        positionedAbove = false;
      } else {
        y = position.y - TOOLBAR_MARGIN;
        positionedAbove = true;
      }
    }
    
    // Clamp horizontal position to stay within viewport
    const toolbarWidth = toolbarSize.width || 400; // Estimated width
    const halfWidth = toolbarWidth / 2;
    x = Math.max(containerBounds.left + halfWidth + VIEWPORT_PADDING, x);
    x = Math.min(containerBounds.right - halfWidth - VIEWPORT_PADDING, x);
    
    return { x, y, positionedAbove };
  };
  
  const calculatedPosition = useMemo(() => calculatePosition(), [
    elementBounds?.x,
    elementBounds?.y,
    elementBounds?.width,
    elementBounds?.height,
    position?.x,
    position?.y,
    toolbarSize.width,
  ]);

  // Update position state when position changes
  useEffect(() => {
    setIsPositionedAbove(calculatedPosition.positionedAbove);
  }, [calculatedPosition.positionedAbove]);
  
  // Determine transform based on position and animation state
  const slideOffset = isPositionedAbove ? '-20px' : '20px';
  const translateY = isVisible ? '0' : slideOffset;
  const transformValue = `translate(-50%, -100%) translateY(${translateY})`;
  
  const toolbarStyle: React.CSSProperties = {
    position: 'absolute',
    left: calculatedPosition.x,
    top: calculatedPosition.y,
    transform: transformValue,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 8px',
    backgroundColor: 'white',
    border: '1px solid rgb(229, 231, 235)',
    borderRadius: '9999px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    zIndex: 10001, // Above text editor
    opacity: isVisible ? 1 : 0,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  };

  const buttonStyle = (isActive: boolean): React.CSSProperties => ({
    width: '32px',
    height: '32px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: isActive ? 'rgba(0, 134, 201, 0.1)' : 'transparent',
    color: isActive ? '#0086C9' : 'rgb(107, 114, 128)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
  });

  const dividerStyle: React.CSSProperties = {
    width: '1px',
    height: '24px',
    backgroundColor: 'rgb(229, 231, 235)',
    margin: '0 4px',
  };

  const presetColors = [
    '#000000', '#374151', '#6B7280', '#9CA3AF',
    '#EF4444', '#F59E0B', '#10B981', '#3B82F6',
    '#8B5CF6', '#EC4899', '#F97316', '#14B8A6',
  ];

  const fontSizes = [10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 64];

  const fontFamilies = [
    'Inter',
    'Arial',
    'Helvetica',
    'Times New Roman',
    'Courier New',
    'Georgia',
    'Verdana',
  ];

  const isBold = textStyle.fontStyle.includes('bold');
  const isItalic = textStyle.fontStyle.includes('italic');

  const handleToolbarAction = (action: () => void) => {
    // Prevent blur on text editor
    action();
  };

  return (
    <div ref={toolbarRef} style={toolbarStyle} data-text-toolbar>
      {/* Font Family */}
      <select
        value={textStyle.fontFamily}
        onChange={(e) => handleToolbarAction(() => onStyleChange({ fontFamily: e.target.value }))}
        style={{
          padding: '4px 8px',
          border: '1px solid rgb(229, 231, 235)',
          borderRadius: '4px',
          fontSize: '13px',
          cursor: 'pointer',
          backgroundColor: 'white',
        }}
      >
        {fontFamilies.map((font) => (
          <option key={font} value={font}>
            {font}
          </option>
        ))}
      </select>

      <div style={dividerStyle} />

      {/* Font Size */}
      <div style={{ position: 'relative' }}>
        <button
          style={buttonStyle(false)}
          onClick={() => setShowFontSizeMenu(!showFontSizeMenu)}
          title="Font Size"
        >
          <Type size={16} />
          <span style={{ fontSize: '12px', marginLeft: '4px' }}>{textStyle.fontSize}</span>
        </button>
        {showFontSizeMenu && (
          <div
            ref={fontSizeMenuRef}
            style={{
              position: 'absolute',
              bottom: '40px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'white',
              border: '1px solid rgb(229, 231, 235)',
              borderRadius: '8px',
              padding: '4px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              zIndex: 10002,
              maxHeight: '200px',
              overflowY: 'auto',
            }}
          >
            {fontSizes.map((size) => (
              <button
                key={size}
                style={{
                  ...buttonStyle(textStyle.fontSize === size),
                  width: '60px',
                  height: '28px',
                  justifyContent: 'flex-start',
                  padding: '0 8px',
                }}
                onClick={() => {
                  handleToolbarAction(() => {
                    onStyleChange({ fontSize: size });
                    setShowFontSizeMenu(false);
                  });
                }}
              >
                {size}
              </button>
            ))}
          </div>
        )}
      </div>

      <div style={dividerStyle} />

      {/* Bold */}
      <button
        style={buttonStyle(isBold)}
        onClick={() => handleToolbarAction(() => {
          const newStyle = isBold
            ? textStyle.fontStyle.replace('bold', '').replace(/\s+/g, ' ').trim() || 'normal'
            : textStyle.fontStyle === 'italic'
            ? 'bold italic'
            : 'bold';
          onStyleChange({ fontStyle: newStyle as TextStyle['fontStyle'] });
        })}
        title="Bold (Ctrl+B)"
      >
        <Bold size={16} strokeWidth={isBold ? 2.5 : 2} />
      </button>

      {/* Italic */}
      <button
        style={buttonStyle(isItalic)}
        onClick={() => handleToolbarAction(() => {
          const newStyle = isItalic
            ? textStyle.fontStyle.replace('italic', '').replace(/\s+/g, ' ').trim() || 'normal'
            : textStyle.fontStyle === 'bold'
            ? 'bold italic'
            : 'italic';
          onStyleChange({ fontStyle: newStyle as TextStyle['fontStyle'] });
        })}
        title="Italic (Ctrl+I)"
      >
        <Italic size={16} strokeWidth={isItalic ? 2.5 : 2} />
      </button>

      <div style={dividerStyle} />

      {/* Text Color */}
      <div style={{ position: 'relative' }}>
        <button
          style={buttonStyle(false)}
          onClick={() => {
            setShowColorPicker(!showColorPicker);
          }}
          title="Text Color"
        >
          <Palette size={16} strokeWidth={2} />
          <div
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '2px',
              backgroundColor: textStyle.fill,
              marginLeft: '4px',
              border: '1px solid rgb(229, 231, 235)',
            }}
          />
        </button>
        {showColorPicker && (
          <div
            ref={colorPickerRef}
            style={{
              position: 'absolute',
              bottom: '40px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'white',
              border: '1px solid rgb(229, 231, 235)',
              borderRadius: '8px',
              padding: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '4px',
              zIndex: 10002,
            }}
          >
            {presetColors.map((color) => (
              <button
                key={color}
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '4px',
                  backgroundColor: color,
                  border: '1px solid rgb(229, 231, 235)',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  handleToolbarAction(() => {
                    onStyleChange({ fill: color });
                    setShowColorPicker(false);
                  });
                }}
              />
            ))}
            <input
              type="color"
              value={textStyle.fill}
              onChange={(e) => handleToolbarAction(() => onStyleChange({ fill: e.target.value }))}
              style={{
                width: '24px',
                height: '24px',
                border: '1px solid rgb(229, 231, 235)',
                borderRadius: '4px',
                cursor: 'pointer',
                padding: '2px',
              }}
              title="Custom Color"
            />
          </div>
        )}
      </div>

      <div style={dividerStyle} />

      {/* Alignment */}
      <button
        style={buttonStyle(textStyle.align === 'left')}
        onClick={() => handleToolbarAction(() => onStyleChange({ align: 'left' }))}
        title="Align Left"
      >
        <AlignLeft size={16} strokeWidth={textStyle.align === 'left' ? 2.5 : 2} />
      </button>
      <button
        style={buttonStyle(textStyle.align === 'center')}
        onClick={() => handleToolbarAction(() => onStyleChange({ align: 'center' }))}
        title="Align Center"
      >
        <AlignCenter size={16} strokeWidth={textStyle.align === 'center' ? 2.5 : 2} />
      </button>
      <button
        style={buttonStyle(textStyle.align === 'right')}
        onClick={() => handleToolbarAction(() => onStyleChange({ align: 'right' }))}
        title="Align Right"
      >
        <AlignRight size={16} strokeWidth={textStyle.align === 'right' ? 2.5 : 2} />
      </button>

      {onClose && (
        <>
          <div style={dividerStyle} />
          <button
            style={buttonStyle(false)}
            onClick={onClose}
            title="Close"
          >
            <X size={16} />
          </button>
        </>
      )}
    </div>
  );
};


